import { MenuList, Paper, TextField, Typography } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import { alpha, useTheme } from '@mui/material/styles';
import isArray from 'lodash/isArray';
import { any, arrayOf, bool, func, object, shape, string } from 'prop-types';
import React, { forwardRef } from 'react';
import { I18n } from 'utils/i18n';
import Select, { components } from 'react-select';
import Creatable from 'react-select/creatable';
import { AsyncPaginate } from 'react-select-async-paginate';

const inputComponent = ({ inputRef, ...props }) => <div ref={inputRef} {...props} />;

const Placeholder = ({ innerProps, children }) => (
  <Typography
    sx={{
      position: 'absolute',
      left: '10px',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontWeight: 'normal',
      fontStyle: 'normal',
      fontStretch: 'normal',
      letterSpacing: '-0.1px',
      color: 'text.secondary',
    }}
    {...innerProps}
  >
    {children}
  </Typography>
);

const Option = ({ innerProps, isDisabled, isSelected, children, data }) => {
  return (
    <MenuItem selected={isSelected} disabled={isDisabled} {...innerProps} title={data?.title}>
      {children}
    </MenuItem>
  );
};

const controlInputStyle = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  minHeight: 18,
  height: 'auto',
  padding: 2,
};

const Control = ({ selectProps, children, innerProps }) => (
  <TextField
    fullWidth
    error={selectProps.error}
    helperText={selectProps.helperText}
    size="small"
    slotProps={{
      input: {
        inputComponent,
        inputProps: {
          children,
          ...innerProps,
          style: { ...controlInputStyle, ...innerProps.style },
        },
      },
    }}
  />
);

const Menu = ({ children, ...props }) => {
  return (
    <components.Menu {...props}>
      <Paper elevation={8}>
        <MenuList disablePadding>{children}</MenuList>
      </Paper>
    </components.Menu>
  );
};

const getOptions = suggestions =>
  suggestions && suggestions[0]?.label
    ? suggestions
    : suggestions?.map(s => ({
        label: s,
        value: s,
      })) || [];

const getValue = (value, suggestions) => {
  if (isArray(value)) {
    return value.map(v => getValue(v, suggestions));
  }
  if (value?.name) {
    return { label: value.name, value };
  }
  // AsyncPaginate / react-select option shape when options are not in memory
  if (
    value &&
    typeof value === 'object' &&
    Object.prototype.hasOwnProperty.call(value, 'label') &&
    Object.prototype.hasOwnProperty.call(value, 'value')
  ) {
    return value;
  }
  if (!suggestions) {
    return undefined;
  }
  const found = suggestions.find(s => {
    return s.value === value || (s.options && getValue(value, s.options));
  });
  if (found) {
    return found;
  }
  return value === '' ? '' : undefined;
};

const Autosuggest = forwardRef(
  (
    {
      onChange,
      value = null,
      suggestions,
      onCreate = () => {},
      creatable = false,
      components: componentsIn,
      classes: classesIn = {},
      loadOptions,
      additional,
      ...inProps
    },
    ref,
  ) => {
    const props = { ...inProps };
    const theme = useTheme();
    if (loadOptions && creatable) {
      throw new Error('Incompatible options');
    }
    let Component;
    if (creatable) {
      Component = Creatable;
    } else if (loadOptions) {
      Component = AsyncPaginate;
      props.defaultOptions = suggestions || true;
    } else {
      Component = Select;
    }
    if (props.disabled) {
      return (
        <TextField
          sx={{ width: '100%', display: 'flex', flexGrow: 1 }}
          value={getValue(value, suggestions)?.label}
          {...props}
        />
      );
    }
    return (
      <Component
        ref={ref}
        menuPlacement="auto"
        classes={classesIn}
        onCreateOption={onCreate}
        className={classesIn.root}
        options={getOptions(suggestions)}
        components={{
          Control,
          Menu,
          Placeholder,
          Option,
          ...componentsIn,
        }}
        placeholder={props.placeholder || I18n.t('SelectPlaceholder')}
        value={getValue(value, suggestions)}
        onChange={onChange}
        {...props}
        styles={{
          menu: provided => ({
            ...provided,
            backgroundColor: 'transparent',
            boxShadow: 'none',
            zIndex: 2,
          }),
          container: provided => ({
            ...provided,
            flexGrow: 1,
            ...(classesIn.root ? {} : { width: '100%', display: 'flex' }),
          }),
          ...props.styles,
        }}
        theme={th => ({
          ...th,
          colors: {
            ...th.colors,
            primary:
              theme.palette.mode === 'dark'
                ? theme.palette.primary.lighter
                : theme.palette.primary.main,
            primary75: theme.palette.primary.lighter,
            primary50: theme.palette.primary.extraLight,
            primary25: theme.palette.primary.ultraLight,
            neutral10: alpha(theme.palette.text.primary, 0.1),
            neutral15: alpha(theme.palette.text.primary, 0.15),
            neutral20: alpha(theme.palette.text.primary, 0.2),
            neutral30: alpha(theme.palette.text.primary, 0.3),
            neutral40: alpha(theme.palette.text.primary, 0.4),
            neutral50: alpha(theme.palette.text.primary, 0.5),
            neutral60: alpha(theme.palette.text.primary, 0.6),
            neutral70: alpha(theme.palette.text.primary, 0.7),
            neutral80: alpha(theme.palette.text.primary, 0.8),
            neutral90: alpha(theme.palette.text.primary, 0.9),
          },
        })}
        loadOptions={loadOptions}
        additional={additional}
      />
    );
  },
);

Autosuggest.propTypes = {
  suggestions: arrayOf(
    shape({
      label: string.isRequired,
      value: string.isRequired,
    }),
  ),
  value: any,
  onChange: func.isRequired,
  onCreate: func,
  creatable: bool,
  classes: object,
  components: object,
  loadOptions: func,
  additional: object,
};

export default Autosuggest;
